import type { BillingProfile } from './billing.model';

export type UserRole = 'owner' | 'user' | 'admin';

/**
 * Bump when the legal documents change. Stored on the user profile at
 * acceptance time for audit/proof.
 */
export const LEGAL_VERSION = '2026-V1';

export interface UserProfile {
  uid:         string;
  email:       string;
  displayName: string;
  photoURL:    string;
  role:        UserRole;
  createdAt:   number;
  /**
   * Public profile slug — set ONLY by the `assignOwnerSlugOnUserWrite` Cloud
   * Function for users with role `owner` or `admin`. Used to build the URL
   * `/propietario/:slug`. Renters (role `user`) never have one.
   */
  ownerSlug?:  string;
  /** True once the user accepted the legal terms during signup. */
  legalAccepted?:   boolean;
  /** Version string of the documents accepted (see LEGAL_VERSION). */
  legalVersion?:    string;
  /** Server timestamp at acceptance. Stored as Firestore Timestamp. */
  legalAcceptedAt?: unknown;
  /** Optional — populated server-side by a Cloud Function (TODO). */
  legalIpAddress?:  string;

  // ── Stripe Connect + commission tier ─────────────────────────────────────
  // Owner-only fields. Writes are gated by firestore.rules to admin/Functions
  // so they cannot be tampered with from the client.
  /** Stripe Connect account ID (acct_...) for direct charges. */
  stripeAccountId?: string;
  stripeConnectStatus?: 'none' | 'onboarding' | 'restricted' | 'active';
  stripeChargesEnabled?: boolean;
  stripePayoutsEnabled?: boolean;
  /**
   * Application-fee rate applied to bookings on this owner's spaces.
   * Stored as decimal (0.10 = 10%). Default tier is DEFAULT_COMMISSION_RATE.
   */
  platform_commission_rate?: number;
  /** Maintained by the platform for volume-based tier decisions. */
  total_active_spaces?: number;

  /**
   * Owner-chosen payout mode driving the checkout flow on their spaces:
   *   - 'MANUAL': user pays only the platform commission online; the rest is
   *     settled in cash at the venue. No Stripe Connect onboarding required.
   *   - 'STRIPE': user pays full rental + security deposit through Stripe.
   *     Funds are routed to the owner's connected account via a Destination
   *     Charge, minus the platform fee. Requires a valid stripeAccountId.
   *
   * Defaults to MANUAL when missing — matches the historical behaviour of
   * owners who signed up before Stripe Connect onboarding existed.
   */
  payout_mode?: PayoutMode;

  /** Billing data — required for owners to publish; optional contract defaults for guests. */
  billingProfile?: BillingProfile;
}

/** Fallback commission when an owner doc has no rate set. */
export const DEFAULT_COMMISSION_RATE = 0.10;
/** Reduced-tier rate awarded once an owner has many active spaces. */
export const VOLUME_TIER_COMMISSION_RATE = 0.05;
/** Active-space count that unlocks the reduced tier (manual review for now). */
export const VOLUME_TIER_THRESHOLD = 10;

/** Discriminator for the dual-mode checkout flow. See UserProfile.payout_mode. */
export type PayoutMode = 'MANUAL' | 'STRIPE';

/**
 * Global owner settings stored at users/{uid} (merged into the profile doc).
 * Holds the payment methods the owner accepts (SEPA, Bizum) for renters.
 *
 * Backwards compatibility:
 *   When `sepaEnabled` / `bizumEnabled` are `undefined`, they are treated
 *   as `true` if the corresponding fields are filled (legacy profiles).
 */
export interface OwnerSettings {
  // ── SEPA (transferencia bancaria) ─────────────────────────────────────
  /** Legal name of the bank account holder. */
  accountHolder: string;
  /**
   * Spanish IBAN — format ES + 22 digits (E.g. ES91 2100 0418 4502 0005 1332).
   * Stored without spaces, validated on write.
   */
  iban: string;
  /** Owner accepts SEPA transfers from renters. Defaults to !!iban. */
  sepaEnabled?: boolean;

  // ── Bizum ─────────────────────────────────────────────────────────────
  /**
   * Spanish mobile number registered for Bizum payments.
   * Must start with 6 or 7, 9 digits total (e.g. 612345678).
   * Used as the destination phone for Bizum transfers from renters.
   */
  mobilePhone: string;
  /** Owner accepts Bizum payments from renters. Defaults to !!mobilePhone. */
  bizumEnabled?: boolean;

  // ── Efectivo ──────────────────────────────────────────────────────────
  /**
   * Owner accepts cash payments at the venue. Pure toggle — no associated
   * fields. Defaults to `false` (opt-in only; cash flows outside the
   * platform so it has to be explicitly enabled by the owner).
   */
  cashEnabled?: boolean;

  /** Present when fetching payment options during the T-48h deposit step. */
  cardHoldAvailable?: boolean;
}

/** FCM push-notification tokens stored on users/{uid}.fcmTokens[]. */
export interface UserPushProfile {
  fcmTokens?: string[];
}

/** Fallback when an owner doc has no payout_mode set. */
export const DEFAULT_PAYOUT_MODE: PayoutMode = 'MANUAL';
